import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import moment from 'moment'
import { maxBy, minBy, pick, uniqBy } from 'lodash'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, Stack } from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { StyledFieldSet, StyledLegend } from '../../../../components/StyledElements'
import { DefaultDialogTitle } from '../../../../components/styled/Dialog'
import { IconButtonMUI } from '../../../../components/styled/Buttons'
import Select from '../../../../components/forms/Select'
import Input from '../../../../components/forms/Input'
import SimpleDatePicker from '../../../../components/forms/SimpleDatePicker'
import { hasValue } from '../../../../utils/NumberUtil'
import { getFullDate, getHour } from '../../../../utils/DateUtil'
import { arrayOf, getSandreList } from '../../../../utils/StoreUtils'
import { getLogin, getSettingInt } from '../../../../utils/SettingUtils'
import { getQualificationSelectOptions, getStatusSelectOptions } from '../../../../utils/StatusUtil'
import { PASTEL_COLOR } from '../../../../utils/constants/ColorTheme'
import { SANDRE } from '../../../../referencial/constants/ReferencialConstants'
import ToastrAction from '../../../../toastr/actions/ToastrAction'
import JobAction from '../../../../import/actions/JobAction'
import HydrometryAction from '../../../actions/HydrometryAction'
import DtoJobParameters from '../../../../import/dto/DtoJobParameters'
import SieauParameterDto from '../../../../administration/dto/SieauParameterDto'
import DtoHydrometricStation from '../../../dto/DtoHydrometricStation'
import DtoHydroMeasures from '../../../dto/chronicMeasures/DtoHydroMeasures'
import CSVGenericPiezoManualPanel from '../../../../piezometry/components/validation/tools/manualImport/CSVGenericPiezoManualPanel'
import IntegrationModeDialog from '../../../../import/components/content/IntegrationModeDialog'
import { CardTitle } from '../../../../components/card/NewCard'

const ImportHydroToolPanel = ({
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    applicationSettings,
    selectedDataType,
    id,
    hydrometricStation,
    hydroMeasuresFormat,
    isChange,
    setIsChange,
    hydroLastMeasures,
    createTechnicalEvent,
}) => {
    const [validateActive, setValidateActive] = useState(false)
    const [jobType, setJobType] = useState('csvAll')
    const [open, setOpen] = useState(false)
    const [job, setJob] = useState(new DtoJobParameters({ import: {
        measureCote: 2,
        producer: getSettingInt(applicationSettings, 'validationDefaultProducer'),
        manager: getSettingInt(applicationSettings, 'validationDefaultManager'),
        validator: getSettingInt(applicationSettings, 'validationDefaultValidator'),
    } }))
    const [fileContent, setFileContent] = useState(null)
    const [fileName, setFileName] = useState(undefined)
    const [, setLogs] = useState([])
    const [selectedJob, setSelectedJob] = useState(null)
    const [display, setDisplay] = useState('cumul')
    const [hydrometerImportedMeasure, setHydrometerImportedMeasure] = useState([])

    const { date: lastMeasureDate } = hydroLastMeasures || { date: moment().valueOf() }

    const dispatch = useDispatch()

    const {
        sandreCodes,
        contributors,
        jobs,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
        contributors: store.ContributorReducer.contributors,
        jobs: store.JobReducer.jobs,
    }), shallowEqual)

    useEffect(() => {
        dispatch(JobAction.fetchJobs())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const qualifications = getQualificationSelectOptions()
    const status = getStatusSelectOptions()

    const displays = [
        { code: 'cumul', label: i18n.cumulate },
        { code: 'addEnd', label: i18n.addEnd },
    ]

    const possibleImports = [
        { value: 'csvAll', label: i18n.CSVGeneric },
    ]

    const hasImportedMeasures = hydrometerImportedMeasure.length > 0

    const onUploadFile = (e) => {
        const reader = new FileReader()
        const file = e.target.files[0]
        if (file) {
            reader.onloadend = (event) => {
                const split = event.target.result.split(',')
                setFileContent(split[1])
            }
            reader.readAsDataURL(file)
            setFileName(file.name)
        }
    }

    const onCancel = () => {
        setHydrometerImportedMeasure([])
        setValidateActive(false)
        setJobType('')
        setOpen(false)
        setJob(new DtoJobParameters())
        setFileContent(null)
        setFileName(undefined)
    }

    const onSave = () => {
        const filteredImportedMeasure = hydrometerImportedMeasure.map(m => {
            if (!m.isNew) {
                return m
            }
            createTechnicalEvent({ mode: 'import' })
            return { ...m, ...job.import }
        })
        if (display === 'addEnd') {
            const measuresToImportFilter = filteredImportedMeasure.filter(f => f.date > lastMeasureDate)
            dispatch(HydrometryAction.updateMeasures(measuresToImportFilter.filter(f => f.date >= startDate && f.date <= endDate))).then(() => setIsChange(!isChange))
        } else if (display === 'cumul') {
            const measuresImport = hydroMeasuresFormat.map(m => ({ ...m, update: true }))
            const measuresToImportFilter = uniqBy([
                ...measuresImport,
                ...filteredImportedMeasure,
            ], m => getFullDate(m.date)).filter(m => m.updated || m.isNew)
            dispatch(HydrometryAction.updateMeasures(measuresToImportFilter.filter(f => f.date >= startDate && f.date <= endDate))).then(() => setIsChange(!isChange))
        }
    }

    const getDialogContent = () => {
        switch (jobType) {
            case 'csvAll':
                return (
                    <CSVGenericPiezoManualPanel
                        job={{ parameters: job }}
                        selectedDataType={selectedDataType}
                        onChangeJob={(changes) => setJob({ ...job, ...changes.parameters })}
                        isEditMode
                        isPiezo={false}
                        hydrometricStation={hydrometricStation}
                    />
                )
            default:
                return i18n.controlErrors
        }
    }

    const importData = () => {
        setLogs([])
        const jobParameters = new DtoJobParameters({
            ...job,
            user: getLogin(),
            jobType,
            fileContent,
            isManualImport: true,
            returnsData: true,
            routingKey: 'import',
            jobExecutionId: -1,
        })
        dispatch(JobAction.executeJobManually(jobParameters, returnData => {
            const measures = (returnData?.measures || []).filter(m => m.dataType === selectedDataType)
            if (measures.length) {
                const importedMeasures = measures.map(measure => ({
                    hydroId: id,
                    status: job.import.status,
                    qualification: job.import.qualification,
                    nature: job.import.nature,
                    updateLogin: getLogin(),
                    initialValue: measure.value,
                    value: measure.value,
                    date: measure.date,
                    validator: job.import.validator,
                    manager: job.import.manager,
                    producer: job.import.producer,
                    initialPoint: undefined,
                    measureMode: 1,
                    dataType: selectedDataType,
                    isNew: true,
                }))
                const minImportDate = minBy(measures, (v) => v.date)
                const maxImportDate = maxBy(measures, (v) => v.date)
                setHydrometerImportedMeasure(importedMeasures)
                setStartDate(minImportDate?.date)
                setEndDate(maxImportDate?.date)
                setValidateActive(true)
                setOpen(false)
            } else {
                dispatch(ToastrAction.warning(i18n.noDataToDisplay))
                setLogs(returnData.logs)
            }
        }))
    }

    const setJobImport = (changes) => setJob({ ...job, import: { ...job.import, ...changes } })

    const applySelectedJob = () => {
        setJob({ ...job, ...pick(JSON.parse(selectedJob.parameters), ['parameters', 'filters', 'dataTypes']) })
        dispatch(ToastrAction.success(i18n.paramsSettingsApplied))
    }

    return (
        <Card>
            <CardTitle title={i18n.importName} />
            <CardContent>
                <Stack spacing={2}>
                    <StyledFieldSet>
                        <StyledLegend>{i18n.fileToImport}</StyledLegend>
                        <Grid container spacing={1}>
                            <Grid size={12}>
                                <Select
                                    value={jobType}
                                    options={possibleImports}
                                    label={i18n.format}
                                    onChange={v => {
                                        setJobType(v)
                                        setSelectedJob(null)
                                    }}
                                    nullLabel='&nbsp;'
                                />
                            </Grid>
                            <Grid size={12}>
                                <StyledFieldSet>
                                    <StyledLegend>{i18n.useExistingTreatment}</StyledLegend>
                                    <Grid container spacing={2} alignItems='center'>
                                        <Grid size={12}>
                                            <Select
                                                label={i18n.job}
                                                options={jobs.filter(j => j.jobType === jobType)}
                                                value={selectedJob?.id}
                                                disabled={!hasValue(jobType)}
                                                onChange={(_, v) => setSelectedJob(v)}
                                                keyValue='id'
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <Button
                                                fullWidth
                                                variant='outlined'
                                                startIcon={<SettingsIcon />}
                                                disabled={!hasValue(selectedJob)}
                                                onClick={applySelectedJob}
                                            >
                                                {i18n.apply}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </StyledFieldSet>
                            </Grid>
                            <Grid container size={12} direction='row' alignItems='center'>
                                <Grid size={6}>
                                    <Button
                                        variant='contained'
                                        fullWidth
                                        component='label'
                                        startIcon={<UploadFileIcon />}
                                    >
                                        {i18n.importLabel}
                                        <input type='file' hidden onChange={onUploadFile} />
                                    </Button>
                                </Grid>
                                <Grid size={4}>
                                    {hasValue(fileName) && (
                                        <span style={{ marginLeft: 10 }}>
                                            {fileName}
                                        </span>
                                    )}
                                </Grid>
                                <Grid size={'auto'}>
                                    <IconButtonMUI
                                        tooltip={i18n.parameters}
                                        disabled={!(hasValue(fileContent) && hasValue(jobType))}
                                        onClick={() => setOpen(true)}
                                        icon='settings'
                                        sx={{ padding: 0.7 }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </StyledFieldSet>

                    {hasImportedMeasures && (
                        <StyledFieldSet>
                            <StyledLegend>{i18n.importParam}</StyledLegend>
                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <SimpleDatePicker
                                        title={i18n.startDate}
                                        value={startDate}
                                        onChange={v => setStartDate(v)}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <Input title={i18n.startHour} value={getHour(startDate)} disabled />
                                </Grid>
                                <Grid size={6}>
                                    <SimpleDatePicker
                                        title={i18n.endDate}
                                        value={endDate}
                                        onChange={v => setEndDate(v)}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <Input title={i18n.endHour} value={getHour(endDate)} disabled />
                                </Grid>
                                <Grid size={12}>
                                    <Select
                                        label={i18n.dataIntegration}
                                        value={display}
                                        options={displays}
                                        onChange={v => setDisplay(v)}
                                        labelSpan={<IntegrationModeDialog />}
                                    />
                                </Grid>
                            </Grid>
                        </StyledFieldSet>
                    )}

                    {hasImportedMeasures && (
                        <StyledFieldSet>
                            <StyledLegend>{i18n.qualificationData}</StyledLegend>
                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <Select
                                        label={i18n.status}
                                        value={job.import.status}
                                        onChange={v => setJobImport({ status: v })}
                                        options={status}
                                        nullLabel='&nbsp;'
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <Select
                                        label={i18n.qualification}
                                        value={job.import.qualification}
                                        onChange={v => setJobImport({ qualification: v })}
                                        options={qualifications}
                                        nullLabel='&nbsp;'
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <Select
                                        label={i18n.nature}
                                        value={job.import.nature}
                                        onChange={v => setJobImport({ nature: v })}
                                        options={getSandreList(sandreCodes, SANDRE.PIEZOMETER_MEASURE_NATURE, true)}
                                        nullLabel='&nbsp;'
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <Select
                                        label={i18n.producer}
                                        value={job.import.producer}
                                        onChange={v => setJobImport({ producer: v })}
                                        options={contributors}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <Select
                                        label={i18n.administrator}
                                        value={job.import.manager}
                                        onChange={v => setJobImport({ manager: v })}
                                        options={contributors}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <Select
                                        label={i18n.validator}
                                        value={job.import.validator}
                                        onChange={v => setJobImport({ validator: v })}
                                        options={contributors}
                                    />
                                </Grid>
                            </Grid>
                        </StyledFieldSet>
                    )}

                    <Grid container justifyContent='center' spacing={4}>
                        <IconButtonMUI
                            tooltip={i18n.cancel}
                            onClick={onCancel}
                            icon='close'
                            disabled={!validateActive}
                            color={PASTEL_COLOR.red}
                        />
                        <IconButtonMUI
                            tooltip={i18n.register}
                            onClick={onSave}
                            icon='save'
                            disabled={!validateActive}
                            className={validateActive ? 'pulse' : ''}
                        />
                    </Grid>

                    <Dialog fullWidth maxWidth='lg' open={open} onClose={() => setOpen(false)}>
                        <DefaultDialogTitle title={i18n.importParam} onClose={() => setOpen(false)} />
                        <DialogContent>
                            {getDialogContent()}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={importData} variant='contained' color='primary'>
                                {i18n.importLabel}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Stack>
            </CardContent>
        </Card>
    )
}

ImportHydroToolPanel.propTypes = {
    startDate: PropTypes.number,
    setStartDate: PropTypes.func,
    endDate: PropTypes.number,
    setEndDate: PropTypes.func,
    applicationSettings: PropTypes.arrayOf(PropTypes.instanceOf(SieauParameterDto)),
    selectedDataType: PropTypes.number,
    id: PropTypes.number,
    hydrometricStation: PropTypes.arrayOf(PropTypes.instanceOf(DtoHydrometricStation)),
    hydroMeasuresFormat: arrayOf(DtoHydroMeasures),
    isChange: PropTypes.bool,
    setIsChange: PropTypes.func,
    hydroLastMeasures: arrayOf(DtoHydroMeasures),
    createTechnicalEvent: PropTypes.func,
}

export default ImportHydroToolPanel